/**
 * PDF generation and branding helper functions.
 * Uses configuration from branding.js
 */

import { ORGANIZATION, PDF, TYPOGRAPHY } from "./branding";

/**
 * Fetch the MSC logo from the public folder and return it as a data URL.
 * Pass white=true to load the SVG and force all paths to white (for use on dark headers).
 * Returns null if the image cannot be loaded.
 */
export async function loadMscLogo(white = false) {
  try {
    if (white) {
      const resp = await fetch("/images/Mohammedan_SC_logo.svg");
      if (!resp.ok) return null;
      let svgText = await resp.text();
      const whiteStyle = "<style>* { fill: white !important; stroke: none !important; }</style>";
      svgText = svgText.replace(/<svg([^>]*)>/, `<svg$1>${whiteStyle}`);
      const blob = new Blob([svgText], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const W = img.naturalWidth || 960;
          const H = img.naturalHeight || 1050;
          const size = 400;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = Math.round((size * H) / W);
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
      });
    }
    const response = await fetch(PDF.footerLogoPath);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Stamp the logo in the bottom-right corner of every page.
 */
export async function addWatermarkToPdf(doc) {
  try {
    const logoData = await loadMscLogo();
    if (!logoData) return;

    const pageCount = doc.getNumberOfPages();
    const s = PDF.footerLogoSize;

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      doc.addImage(logoData, "PNG", pageW - s - 5, pageH - s - 4, s, s);
    }
  } catch {
    // Logo is decorative; never block PDF generation
  }
}

/**
 * Create and return a landscape A4 jsPDF document.
 */
export async function buildPdfDoc() {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  return doc;
}

/**
 * Add a branded header block to an existing jsPDF document.
 * Stores the Y-coordinate after the header in `doc.__brandedHeaderY`.
 */
export function addBrandedHeader(doc, reportTitle, metaLines = [], logoData = null) {
  const now = new Date().toLocaleString("en-IN");
  const pageW = doc.internal.pageSize.getWidth();
  const logoSize = 16;
  const logoX = 8;
  const textStartX = logoData ? logoX + logoSize + 4 : 14;

  doc.setFillColor(...PDF.brandColor);
  doc.rect(0, 0, pageW, PDF.headerBandHeight, "F");

  if (logoData) {
    try { doc.addImage(logoData, "PNG", logoX, 3, logoSize, logoSize); } catch { /* decorative */ }
  }

  doc.setTextColor(...PDF.headerTextColor);
  doc.setFontSize(13);
  doc.setFont(TYPOGRAPHY.fonts.default, "bold");
  doc.text(ORGANIZATION.name, textStartX, 12);

  if (ORGANIZATION.tagline) {
    doc.setFontSize(8);
    doc.setFont(TYPOGRAPHY.fonts.default, "normal");
    doc.text(ORGANIZATION.tagline, textStartX, 18);
  }

  doc.setFontSize(7);
  doc.text(`Generated: ${now}`, pageW - 14, 18, { align: "right" });

  doc.setTextColor(...TYPOGRAPHY.reportTitle.color);
  doc.setFontSize(TYPOGRAPHY.reportTitle.fontSize);
  doc.setFont(TYPOGRAPHY.fonts.default, "bold");
  doc.text(reportTitle, 14, 30);

  let y = 35;
  doc.setFontSize(TYPOGRAPHY.reportMeta.fontSize);
  doc.setFont(TYPOGRAPHY.fonts.default, "normal");
  doc.setTextColor(TYPOGRAPHY.reportMeta.color);
  for (const line of metaLines) {
    doc.text(line, 14, y);
    y += 5;
  }

  doc.__brandedHeaderY = y + 2;
}

/**
 * Build a PDF doc with branded header.
 * Returns { doc, autoTable, startY, headStyles, altStyles } ready for table data.
 */
export async function buildBrandedReport(reportTitle, metaLines = []) {
  const [doc, autoTableMod, logoData] = await Promise.all([
    buildPdfDoc(),
    import("jspdf-autotable"),
    loadMscLogo(true),
  ]);
  const autoTable = autoTableMod.default;

  addBrandedHeader(doc, reportTitle, metaLines, logoData);

  return {
    doc,
    autoTable,
    startY: doc.__brandedHeaderY ?? 42,
    headStyles: { fillColor: PDF.brandColor, textColor: PDF.headerTextColor, fontStyle: "bold" },
    altStyles: { fillColor: PDF.accentColor },
  };
}
